import { Tile } from '../tiles';
import { toCounts } from '../hand';
import { isAgari } from '../agari';
import { scoreWin } from '../scoring/win';
import { isTenpai, isFuriten } from '../waits';
import { sortHand } from './state';
import { buildScoreContext } from './legal';
import { GameState, GameAction, PlayerState, HandResult } from './types';

function clonePlayers(players: PlayerState[]): PlayerState[] {
  return players.map((p) => ({ ...p, hand: p.hand.slice(), discards: p.discards.slice() }));
}

/** lastDiscard에 론 가능한 상대 자리(버린 사람 다음 순서)를 계산.
 *  legal.ts는 reduce.ts를 import하지 않으므로 순환 의존은 없다. */
function findResponders(state: GameState, tile: Tile, from: number): number[] {
  const out: number[] = [];
  for (let step = 1; step <= 3; step++) {
    const seat = (from + step) % 4;
    const counts = toCounts([...state.players[seat].hand, tile]);
    if (!isAgari(counts)) continue;                                   // 화료형
    if (isFuriten(state.players[seat].hand, state.players[seat].discards)) continue; // 후리텐 금지
    const ctx = buildScoreContext(state, seat, tile, false);
    if (scoreWin(counts, ctx).valid) out.push(seat);                 // 役 성립
  }
  return out;
}

/** 유국 정산: 텐파이/노텐 3000 분배(있을 때). 리치봉은 탁에 남김. */
function settleRyuukyoku(state: GameState): GameState {
  const players = clonePlayers(state.players);
  const tenpaiSeats: number[] = [];
  for (let i = 0; i < 4; i++) if (isTenpai(players[i].hand)) tenpaiSeats.push(i);
  const t = tenpaiSeats.length;
  if (t > 0 && t < 4) {
    const noten = 4 - t;
    const gain = 3000 / t;
    const loss = 3000 / noten;
    for (let i = 0; i < 4; i++) {
      players[i] = { ...players[i], points: players[i].points + (tenpaiSeats.includes(i) ? gain : -loss) };
    }
  }
  const result: HandResult = { type: 'ryuukyoku', tenpaiSeats };
  return { ...state, players, phase: 'ended', result, drawn: null };
}

function applyWinPayments(
  state: GameState,
  winner: number,
  isTsumo: boolean,
  winningTile: Tile,
  loser: number | null,
): GameState {
  const counts = toCounts([...state.players[winner].hand, winningTile]);
  const ctx = buildScoreContext(state, winner, winningTile, isTsumo);
  const score = scoreWin(counts, ctx);
  if (!score.valid) return state; // 役 없는 화료는 성립하지 않음(방어)
  const players = clonePlayers(state.players);

  const pay = score.payment;
  if (isTsumo) {
    let collected = 0;
    for (let i = 0; i < 4; i++) {
      if (i === winner) continue;
      const isDealerPayer = i === state.dealer;
      const amount = ctx.isDealer ? pay.tsumoEach! : (isDealerPayer ? pay.tsumoDealer! : pay.tsumoEach!);
      players[i] = { ...players[i], points: players[i].points - amount };
      collected += amount;
    }
    players[winner] = { ...players[winner], points: players[winner].points + collected };
  } else {
    const amount = pay.ron!;
    players[loser!] = { ...players[loser!], points: players[loser!].points - amount };
    players[winner] = { ...players[winner], points: players[winner].points + amount };
  }
  // 리치봉(공탁) 회수
  players[winner] = { ...players[winner], points: players[winner].points + state.riichiSticks * 1000 };

  const result: HandResult = {
    type: isTsumo ? 'tsumo' : 'ron',
    winner,
    loser: loser ?? undefined,
    score,
    winningTile,
  };
  return { ...state, players, phase: 'ended', result, riichiSticks: 0, drawn: null };
}

export function applyAction(state: GameState, action: GameAction): GameState {
  if (state.phase === 'ended') return state;

  switch (action.type) {
    case 'draw': {
      if (state.phase !== 'draw') return state;
      if (state.wall.length === 0) return settleRyuukyoku(state);
      const drawn = state.wall[0];
      return { ...state, wall: state.wall.slice(1), drawn, phase: 'discard' };
    }

    case 'discard':
    case 'riichi': {
      if (state.phase !== 'discard' || state.drawn === null) return state;
      const seat = state.turn;
      const players = clonePlayers(state.players);
      const p = players[seat];
      // 14장에서 버릴 패 제거
      const all = [...p.hand, state.drawn];
      const idx = all.indexOf(action.tile);
      if (idx === -1) return state;
      all.splice(idx, 1);
      p.hand = sortHand(all);
      p.discards = [...p.discards, action.tile];

      let riichiSticks = state.riichiSticks;
      if (action.type === 'riichi') {
        p.riichi = true;
        p.riichiIndex = p.discards.length - 1;
        p.points -= 1000;
        riichiSticks += 1;
      }
      players[seat] = p;

      const baseNext: GameState = {
        ...state, players, drawn: null,
        lastDiscard: { tile: action.tile, from: seat }, riichiSticks,
      };
      const responders = findResponders(baseNext, action.tile, seat);
      if (responders.length > 0) {
        return { ...baseNext, phase: 'response', responders };
      }
      return { ...baseNext, phase: 'draw', turn: (seat + 1) % 4, responders: [] };
    }

    case 'tsumo': {
      if (state.phase !== 'discard' || state.drawn === null) return state;
      return applyWinPayments(state, state.turn, true, state.drawn, null);
    }

    case 'ron': {
      if (state.phase !== 'response' || state.lastDiscard === null) return state;
      // 아타마하네: 방총자에 가장 가까운(responders[0]) 응답자만 론 성립
      if (state.responders[0] !== action.player) return state;
      return applyWinPayments(state, action.player, false, state.lastDiscard.tile, state.lastDiscard.from);
    }

    case 'pass': {
      if (state.phase !== 'response') return state;
      const responders = state.responders.filter((s) => s !== action.player);
      if (responders.length > 0) return { ...state, responders };
      // 모두 패스 → 다음 차례 draw
      const from = state.lastDiscard!.from;
      return { ...state, phase: 'draw', turn: (from + 1) % 4, responders: [] };
    }

    default:
      return state;
  }
}
